import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { mediaUrl } from "../lib/api";
import { logError } from "../lib/log";
import { useAuth } from "../context/AuthContext";
import { useLang, catLabel } from "../lib/i18n";
import { LangToggle } from "../components/LangToggle";
import { StatusBadge } from "../components/StatusBadge";
import { OrderTimeline } from "../components/OrderTimeline";
import { QuoteCard } from "../components/QuoteCard";
import { PhotoUploader } from "../components/PhotoUploader";
import { PayPalPay } from "../components/PayPalPay";
import { PaymentBadges } from "../components/PaymentBadges";
import { ArrowLeft, Phone, MapPin, Loader2, Star, CreditCard, CheckCircle2, MessageSquare, TriangleAlert } from "lucide-react";

export default function OrderDetail() {
  const { id } = useParams();
  const { t, lang } = useLang();
  const { user } = useAuth();
  const nav = useNavigate();
  const [order, setOrder] = useState(null);
  const [busy, setBusy] = useState(false);
  const [stars, setStars] = useState(0);
  const [comment, setComment] = useState("");
  const [finalPhotos, setFinalPhotos] = useState([]);
  const [showVar, setShowVar] = useState(false);
  const [varReason, setVarReason] = useState("");
  const [varAmount, setVarAmount] = useState("");

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/orders/${id}`); setOrder(data); setFinalPhotos(data.final_photos || []); }
    catch { nav(-1); }
  }, [id, nav]);

  useEffect(() => { load(); }, [load]);

  const isTech = user?.role === "tecnico";
  const isClient = user?.role === "cliente";

  const act = async (fn) => { setBusy(true); try { await fn(); await load(); } catch (e) { logError(e); } finally { setBusy(false); } };

  const acceptQuote = () => act(() => api.post(`/orders/${id}/accept-quote`));
  const rejectQuote = () => act(() => api.post(`/orders/${id}/reject-quote`));
  const submitRating = () => act(() => api.post(`/orders/${id}/rate`, { stars, comment }));
  const techStatus = (action) => act(() => api.post(`/orders/${id}/tech-status`, { action }));
  const saveFinal = () => act(() => api.post(`/orders/${id}/final-photos`, { photos: finalPhotos }));
  const requestVariation = () => act(async () => { await api.post(`/orders/${id}/request-variation`, { reason: varReason, amount: parseFloat(varAmount) }); setShowVar(false); setVarReason(""); setVarAmount(""); });
  const acceptVariation = () => act(() => api.post(`/orders/${id}/accept-variation`));
  const rejectVariation = () => act(() => api.post(`/orders/${id}/reject-variation`));
  const confirmAccess = () => act(() => api.post(`/orders/${id}/tech-confirm-access`));

  const pay = async () => {
    setBusy(true);
    try { const { data } = await api.post("/payments/checkout", { order_id: id, origin_url: window.location.origin }); window.location.href = data.checkout_url; }
    catch (e) { logError(e); setBusy(false); }
  };

  if (!order) return <div className="flex min-h-screen items-center justify-center bg-slate-50"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>;

  return (
    <div className="mx-auto min-h-screen max-w-md bg-slate-50 pb-8">
      <header className="sticky top-0 z-30 flex items-center justify-between border-b border-slate-200 bg-white px-4 py-3">
        <button data-testid="od-back" onClick={() => nav(isTech ? "/tecnico" : "/area-clienti/ordini")} className="text-slate-500"><ArrowLeft /></button>
        <span className="font-mono text-sm font-bold text-blue-600">{order.code}</span>
        <LangToggle />
      </header>

      <div className="space-y-4 p-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-display text-xl font-extrabold text-slate-900">{order.problem}</h1>
            <p className="text-sm text-slate-500">{catLabel(order.category, lang)}</p>
          </div>
          <StatusBadge status={order.status} />
        </div>
        {order.is_demo && <div className="rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-bold text-slate-500">DEMO — {t("app_tagline")}</div>}

        {order.photos?.length > 0 && (
          <div className="flex gap-2 overflow-x-auto no-scrollbar">
            {order.photos.map((p, i) => <img key={i} src={mediaUrl(p)} alt="" className="h-24 w-24 rounded-xl object-cover" />)}
          </div>
        )}

        <div className="rounded-2xl border border-slate-200 bg-white p-4 text-sm">
          <p className="text-slate-700">{order.description}</p>
          <div className="mt-3 flex items-start gap-2 text-slate-600"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" /><span>{order.address?.street}, {order.address?.city}{order.address?.details ? ` — ${order.address.details}` : ""}</span></div>
          {order.time_slot && <p className="mt-2 text-xs text-slate-500">{t("time_slot")}: <b>{order.time_slot}</b></p>}
          {isTech && <p className="mt-1 text-xs text-slate-500">{t("phone")}: <a href={`tel:${order.phone}`} className="font-bold text-blue-600">{order.phone}</a></p>}
          {!order.in_zone && <div className="mt-3 flex items-center gap-2 rounded-lg bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800"><TriangleAlert className="h-4 w-4" />{t("out_of_zone_warn")}</div>}
        </div>

        {order.quote && <QuoteCard quote={order.quote} />}

        {!isTech && order.price_variation && (
          <div className="rounded-xl border border-orange-300 bg-orange-50 p-3 text-sm text-orange-800">
            <p className="font-bold">{t("variation_pending")}</p>
            <p>{order.price_variation.reason} — € {Number(order.price_variation.amount).toFixed(2)} ({order.price_variation.status})</p>
          </div>
        )}

        {/* CLIENT: accept/reject a proposed price variation */}
        {isClient && order.price_variation?.status === "proposta" && (
          <div className="rounded-2xl border-2 border-orange-400 bg-orange-50 p-4" data-testid="client-variation-box">
            <p className="font-display text-base font-bold text-orange-800">{t("variation_proposed")}</p>
            <p className="mt-1 text-sm text-orange-700">{order.price_variation.reason}</p>
            <p className="mt-1 text-2xl font-extrabold text-orange-900">+ € {Number(order.price_variation.amount).toFixed(2)}</p>
            <div className="mt-3 flex gap-3">
              <button data-testid="accept-variation-btn" disabled={busy} onClick={acceptVariation} className="flex-1 rounded-xl bg-emerald-600 py-3 font-bold text-white active:scale-[0.98]">{t("accept_variation")}</button>
              <button data-testid="reject-variation-btn" disabled={busy} onClick={rejectVariation} className="flex-1 rounded-xl border border-rose-300 bg-white py-3 font-bold text-rose-600">{t("reject_variation")}</button>
            </div>
          </div>
        )}

        {/* CLIENT: accept/reject quote */}
        {isClient && order.status === "preventivo_inviato" && (
          <div className="flex gap-3">
            <button data-testid="accept-quote-btn" disabled={busy} onClick={acceptQuote} className="flex-1 rounded-xl bg-emerald-600 py-3 font-bold text-white transition hover:bg-emerald-700 active:scale-[0.98]">{t("accept_quote")}</button>
            <button data-testid="reject-quote-btn" disabled={busy} onClick={rejectQuote} className="flex-1 rounded-xl border border-rose-300 bg-white py-3 font-bold text-rose-600">{t("reject_quote")}</button>
          </div>
        )}

        {/* CLIENT: contact office when in progress */}
        {isClient && ["accettato", "tecnico_assegnato", "in_partenza", "sul_posto", "in_lavorazione", "variazione_da_approvare"].includes(order.status) && (
          <a data-testid="contact-central-btn" href="tel:+390600000000" className="flex items-center justify-center gap-2 rounded-xl border border-blue-200 bg-blue-50 py-3 font-bold text-blue-700"><Phone className="h-4 w-4" />{t("contact_central")}</a>
        )}

        {/* CLIENT: pay when completed */}
        {isClient && order.status === "completato" && (
          <div className="space-y-3" data-testid="payment-options">
            <button data-testid="pay-now-btn" disabled={busy} onClick={pay} className="flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 py-3.5 font-bold text-white shadow-lg shadow-blue-600/25 transition hover:bg-blue-700 active:scale-[0.98]">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-5 w-5" />}{t("pay_card")} — € {Number(order.quote?.total || 0).toFixed(2)}
            </button>
            <PayPalPay orderId={id} onPaid={load} />
            <PaymentBadges />
          </div>
        )}

        {/* CLIENT: receipt + rating when paid */}
        {isClient && order.status === "pagato" && (
          <>
            <div data-testid="receipt" className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
              <div className="flex items-center gap-2 text-emerald-800"><CheckCircle2 className="h-5 w-5" /><span className="font-bold">{t("receipt")}</span></div>
              <p className="mt-2 text-sm text-emerald-700">{t("total")}: <b>€ {Number(order.payment?.amount || order.quote?.total || 0).toFixed(2)}</b></p>
              <p className="font-mono text-xs text-emerald-600">{order.code}</p>
            </div>
            {order.rating ? (
              <div className="rounded-2xl border border-slate-200 bg-white p-4 text-sm">
                <p className="font-bold text-slate-800">{t("thanks_rating")}</p>
                <div className="mt-1 flex gap-0.5">{[1, 2, 3, 4, 5].map((s) => <Star key={s} className={`h-5 w-5 ${s <= order.rating.stars ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />)}</div>
                {order.rating.comment && <p className="mt-1 text-slate-500">{order.rating.comment}</p>}
              </div>
            ) : (
              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <p className="font-bold text-slate-800">{t("rate_work")}</p>
                <p className="text-xs text-slate-500">{t("review_help")}</p>
                <div className="mt-2 flex gap-1">{[1, 2, 3, 4, 5].map((s) => <button key={s} data-testid={`star-${s}`} onClick={() => setStars(s)}><Star className={`h-8 w-8 ${s <= stars ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} /></button>)}</div>
                <textarea data-testid="rating-comment" rows={2} value={comment} onChange={(e) => setComment(e.target.value)} placeholder={t("leave_comment")} className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-500" />
                <button data-testid="submit-rating-btn" disabled={busy || !stars} onClick={submitRating} className="mt-2 w-full rounded-xl bg-blue-600 py-2.5 font-bold text-white disabled:opacity-40">{t("submit_rating")}</button>
              </div>
            )}
          </>
        )}

        {/* TECH controls */}
        {isTech && (
          <div className="space-y-3">
            {order.category === "porta_serratura" && (
              <div className="rounded-2xl border border-amber-300 bg-amber-50 p-4 text-sm" data-testid="tech-access-box">
                <p className="font-bold text-amber-900">{t("access_verification")}</p>
                <p className="mt-1 text-xs text-amber-800">{t("disclaimer_verification")}</p>
                {order.access_verified_by_tech || order.tech_access_confirmed ? null : null}
                <button data-testid="tech-confirm-access-btn" disabled={busy} onClick={confirmAccess} className="mt-2 w-full rounded-xl bg-amber-600 py-2.5 text-sm font-bold text-white active:scale-[0.98]">{t("confirm_access_onsite")}</button>
              </div>
            )}
            {order.status === "tecnico_assegnato" && !order.tech_accepted && <TechBtn testid="tech-accept" onClick={() => techStatus("accetta")} busy={busy} label={t("accept_job")} color="bg-blue-600" />}
            {order.status === "tecnico_assegnato" && order.tech_accepted && <TechBtn testid="tech-departing" onClick={() => techStatus("in_partenza")} busy={busy} label={t("departing")} color="bg-indigo-600" />}
            {order.status === "in_partenza" && <TechBtn testid="tech-arrived" onClick={() => techStatus("arrivato")} busy={busy} label={t("arrived")} color="bg-teal-600" />}
            {order.status === "sul_posto" && <TechBtn testid="tech-started" onClick={() => techStatus("iniziato")} busy={busy} label={t("work_started")} color="bg-amber-600" />}
            {order.status === "in_lavorazione" && <TechBtn testid="tech-completed" onClick={() => techStatus("completato")} busy={busy} label={t("work_completed")} color="bg-emerald-600" />}

            {["in_lavorazione", "completato", "pagato", "variazione_da_approvare"].includes(order.status) && (
              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <p className="mb-2 text-sm font-bold text-slate-800">{t("final_photos")}</p>
                <PhotoUploader photos={finalPhotos} setPhotos={setFinalPhotos} testid="tech-final-photos" />
                <button data-testid="save-final-photos" disabled={busy} onClick={saveFinal} className="mt-3 w-full rounded-xl border border-blue-200 bg-blue-50 py-2.5 text-sm font-bold text-blue-700">{t("save")}</button>
              </div>
            )}

            {["sul_posto", "in_lavorazione"].includes(order.status) && (
              <div className="rounded-2xl border border-orange-200 bg-white p-4">
                {!showVar ? (
                  <button data-testid="request-variation-btn" onClick={() => setShowVar(true)} className="flex w-full items-center justify-center gap-2 text-sm font-bold text-orange-600"><TriangleAlert className="h-4 w-4" />{t("request_variation")}</button>
                ) : (
                  <div className="space-y-2">
                    <input data-testid="var-reason" value={varReason} onChange={(e) => setVarReason(e.target.value)} placeholder={t("variation_reason")} className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-orange-500" />
                    <input data-testid="var-amount" type="number" value={varAmount} onChange={(e) => setVarAmount(e.target.value)} placeholder={t("variation_amount")} className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-orange-500" />
                    <button data-testid="var-submit" disabled={busy || !varReason || !varAmount} onClick={requestVariation} className="w-full rounded-xl bg-orange-600 py-2.5 text-sm font-bold text-white disabled:opacity-40">{t("request_variation")}</button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {order.final_photos?.length > 0 && isClient && (
          <div><p className="mb-2 text-sm font-bold text-slate-700">{t("final_photos")}</p>
            <div className="flex gap-2 overflow-x-auto no-scrollbar">{order.final_photos.map((p, i) => <img key={i} src={mediaUrl(p)} alt="" className="h-24 w-24 rounded-xl object-cover" />)}</div>
          </div>
        )}

        {isClient && !["pagato", "completato", "rifiutato", "annullato"].includes(order.status) && (
          <button data-testid="cancel-order-btn" disabled={busy} onClick={() => act(() => api.post(`/orders/${id}/cancel`))} className="w-full py-2 text-center text-xs font-semibold text-slate-400">{t("cancel")}</button>
        )}

        {/* Lock details (customer + assigned technician) */}
        {order.lock_details && (
          <div className="rounded-2xl border border-slate-200 bg-white p-4 text-sm" data-testid="lock-details">
            <p className="mb-2 font-bold text-slate-800">{t("lock_details")}</p>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <span className="text-slate-500">{t("door_state")}</span><span className="font-semibold">{t("door_" + (order.lock_details.door_state === "chiusa_a_chiave" ? "chiusa" : "accostata"))}</span>
              <span className="text-slate-500">{t("lock_issue")}</span><span className="font-semibold">{t("issue_" + order.lock_details.issue)}</span>
              <span className="text-slate-500">{t("door_type")}</span><span className="font-semibold">{t("door_" + order.lock_details.door_type)}</span>
              <span className="text-slate-500">{t("relationship")}</span><span className="font-semibold">{t("rel_" + order.lock_details.relationship)}</span>
            </div>
          </div>
        )}

        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <p className="mb-3 text-sm font-bold text-slate-800">{t("progress")}</p>
          <OrderTimeline status={order.status} />
        </div>
      </div>
    </div>
  );
}

const TechBtn = ({ testid, onClick, busy, label, color }) => (
  <button data-testid={testid} disabled={busy} onClick={onClick} className={`flex w-full items-center justify-center gap-2 rounded-xl ${color} py-4 text-base font-bold text-white transition active:scale-[0.98] disabled:opacity-60`}>
    {busy && <Loader2 className="h-4 w-4 animate-spin" />}{label}
  </button>
);
